/**
 * Admin — Carrier Tariff Rules CRUD.
 * Route: /admin/carrier-tariffs
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { Plus, X, Trash2 } from "lucide-react";

const SCOPES = [
  { value: "CITY",     label: "Città",    priority: 1 },
  { value: "PROVINCE", label: "Provincia", priority: 2 },
  { value: "REGION",   label: "Regione",  priority: 3 },
];

const SCOPE_TONE = { CITY: "success", PROVINCE: "info", REGION: "neutral" };
const WEEKDAYS = [
  { v: 1, l: "Lun" }, { v: 2, l: "Mar" }, { v: 3, l: "Mer" },
  { v: 4, l: "Gio" }, { v: 5, l: "Ven" }, { v: 6, l: "Sab" }, { v: 7, l: "Dom" },
];

function fmt(v) { return v == null ? "—" : String(v); }
function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : "—"; }
function fmtEur(v) { return v == null ? "—" : `€ ${Number(v).toFixed(2).replace(".", ",")}`; }

export function AdminCarrierTariffsPage() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [rows, setRows] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null);   // null | { mode:'new'|'edit', row? }
  const [filterCarrier, setFilterCarrier] = useState("");
  const [filterScope, setFilterScope] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const rs = await apiGet("/api/admin/carrier-tariffs" +
        (filterCarrier ? `?carrier_id=${filterCarrier}` : "") +
        (filterScope ? `${filterCarrier ? "&" : "?"}scope=${filterScope}` : ""));
      setRows(rs || []);
    } catch (e) { setErr(e?.data?.detail || e?.message || String(e)); setRows([]); }
  };

  useEffect(() => {
    (async () => {
      const cs = await apiGet("/api/carriers");
      const items = cs.items || cs;
      setCarriers(items.filter((c) => c.is_active));
      await load();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCarrier, filterScope]);

  const carrierName = (id) => carriers.find((c) => c.id === id)?.business_name || id.slice(0, 8);

  const del = async (r) => {
    if (!window.confirm(`Archiviare la tariffa (${r.scope} ${r.city || r.province || r.region})?`)) return;
    await apiDelete(`/api/admin/carrier-tariffs/${r.id}`);
    await load();
  };

  return (
    <Section title="Tariffe Vettori"
             subtitle="Gerarchia: CITY > PROVINCE > REGION — la regola più specifica applicabile prevale sempre."
             actions={
               <div style={{ display: "flex", gap: 8 }}>
                 <Link to="/admin"><Button size="sm">← Home Admin</Button></Link>
                 <Button variant="primary" size="sm"
                         onClick={() => setModal({ mode: "new" })}
                         data-testid="tariff-new-btn">
                   <Plus size={14} /> Nuova regola
                 </Button>
               </div>
             }
             data-testid="admin-carrier-tariffs-page">
      {err && <Alert tone="danger">Errore: {String(err)}</Alert>}

      <Card>
        <Card.Body style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 220 }}>
            <Label>Filtro vettore</Label>
            <select data-testid="tariff-filter-carrier"
                    value={filterCarrier} onChange={(e) => setFilterCarrier(e.target.value)}
                    style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
              <option value="">Tutti i vettori</option>
              {carriers.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            <Label>Filtro livello</Label>
            <select data-testid="tariff-filter-scope"
                    value={filterScope} onChange={(e) => setFilterScope(e.target.value)}
                    style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
              <option value="">Tutti i livelli</option>
              {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </Card.Body>
      </Card>

      <div style={{ marginTop: 16 }}>
        {!rows ? <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>
         : rows.length === 0 ? <Card><Card.Body className="muted">Nessuna regola configurata.</Card.Body></Card>
         : (
          <Card>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                    <th style={{ padding: 8 }}>Livello</th>
                    <th style={{ padding: 8 }}>Località</th>
                    <th style={{ padding: 8 }}>Vettore</th>
                    <th style={{ padding: 8 }}>€/pallet</th>
                    <th style={{ padding: 8 }}>Min €</th>
                    <th style={{ padding: 8 }}>Scaglioni</th>
                    <th style={{ padding: 8 }}>Giorni consegna</th>
                    <th style={{ padding: 8 }}>Validità</th>
                    <th style={{ padding: 8 }}>Attiva</th>
                    <th style={{ padding: 8 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`tariff-row-${r.id}`}
                        style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 8 }}>
                        <Badge tone={SCOPE_TONE[r.scope] || "neutral"}>
                          {SCOPES.find((s) => s.value === r.scope)?.label || r.scope}
                        </Badge>
                      </td>
                      <td style={{ padding: 8 }}><strong>{r.city || r.province || r.region}</strong></td>
                      <td style={{ padding: 8 }}>{carrierName(r.carrier_id)}</td>
                      <td style={{ padding: 8 }}>{fmtEur(r.cost_per_pallet)}</td>
                      <td style={{ padding: 8 }}>{fmtEur(r.min_charge)}</td>
                      <td style={{ padding: 8 }}>{r.pallet_tiers?.length || 0}</td>
                      <td style={{ padding: 8 }}>{r.delivery_days_min}–{r.delivery_days_max} gg</td>
                      <td style={{ padding: 8, fontSize: ".8rem" }}>
                        {fmtDate(r.valid_from)} → {r.valid_to ? fmtDate(r.valid_to) : "∞"}
                      </td>
                      <td style={{ padding: 8 }}>
                        <Badge tone={r.is_active ? "success" : "neutral"}>{r.is_active ? "SÌ" : "NO"}</Badge>
                      </td>
                      <td style={{ padding: 8, whiteSpace: "nowrap" }}>
                        <Button size="sm"
                                onClick={() => setModal({ mode: "edit", row: r })}
                                data-testid={`tariff-edit-${r.id}`}>Modifica</Button>{" "}
                        <Button size="sm" variant="danger"
                                onClick={() => del(r)}
                                data-testid={`tariff-delete-${r.id}`}>
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
         )}
      </div>

      {modal && (
        <TariffModal mode={modal.mode} row={modal.row}
                     carriers={carriers}
                     onClose={() => setModal(null)}
                     onSaved={async () => { setModal(null); await load(); }}
                     apiPost={apiPost} apiPatch={apiPatch} />
      )}
    </Section>
  );
}


function TariffModal({ mode, row, carriers, onClose, onSaved, apiPost, apiPatch }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => ({
    carrier_id: row?.carrier_id || carriers[0]?.id || "",
    scope: row?.scope || "REGION",
    city: row?.city || "",
    province: row?.province || "",
    region: row?.region || "",
    cost_per_pallet: row?.cost_per_pallet ?? 25,
    pallet_tiers: row?.pallet_tiers || [],
    min_charge: row?.min_charge ?? "",
    delivery_days_min: row?.delivery_days_min ?? 1,
    delivery_days_max: row?.delivery_days_max ?? 2,
    service_days: row?.service_days || [1, 2, 3, 4, 5],
    pickup_cutoff_hours: row?.pickup_cutoff_hours ?? null,
    pickup_cutoff_time: row?.pickup_cutoff_time ?? null,
    valid_from: (row?.valid_from ? row.valid_from.slice(0, 10) : "2026-01-01"),
    valid_to: (row?.valid_to ? row.valid_to.slice(0, 10) : ""),
    is_active: row?.is_active ?? true,
    notes: row?.notes || "",
  }));
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const p = {
        ...form,
        cost_per_pallet: Number(form.cost_per_pallet),
        min_charge: form.min_charge === "" || form.min_charge == null ? null : Number(form.min_charge),
        delivery_days_min: Number(form.delivery_days_min),
        delivery_days_max: Number(form.delivery_days_max),
        pallet_tiers: form.pallet_tiers.length ? form.pallet_tiers : null,
        valid_to: form.valid_to || null,
      };
      // Only send the location matching scope
      if (p.scope !== "CITY") p.city = null;
      if (p.scope !== "PROVINCE") p.province = null;
      if (p.scope !== "REGION") p.region = null;
      if (isEdit) await apiPatch(`/api/admin/carrier-tariffs/${row.id}`, p);
      else await apiPost("/api/admin/carrier-tariffs", p);
      await onSaved();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || String(e));
      setBusy(false);
    }
  };

  const addTier = () => {
    upd("pallet_tiers", [...form.pallet_tiers, { up_to: null, cost_per_pallet: form.cost_per_pallet || 25 }]);
  };
  const updTier = (i, k, v) => {
    const t = [...form.pallet_tiers];
    t[i] = { ...t[i], [k]: k === "up_to" ? (v === "" ? null : Number(v)) : Number(v) };
    upd("pallet_tiers", t);
  };
  const rmTier = (i) => upd("pallet_tiers", form.pallet_tiers.filter((_, j) => j !== i));

  return (
    <div role="dialog" data-testid="tariff-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 100,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 8, width: "95%", maxWidth: 680,
                    maxHeight: "92vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>{isEdit ? "Modifica tariffa" : "Nuova tariffa"}</h3>
          <Button size="sm" onClick={onClose}><X size={14} /></Button>
        </div>
        {err && <Alert tone="danger">Errore: {String(err)}</Alert>}
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <Label>Vettore</Label>
              <select data-testid="tariff-form-carrier"
                      value={form.carrier_id} onChange={(e) => upd("carrier_id", e.target.value)}
                      disabled={isEdit}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                {carriers.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </select>
            </div>
            <div>
              <Label>Livello geografico</Label>
              <select data-testid="tariff-form-scope"
                      value={form.scope} onChange={(e) => upd("scope", e.target.value)}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label} (priorità {s.priority})</option>
                ))}
              </select>
            </div>
          </div>
          {form.scope === "CITY" && (
            <div><Label>Città</Label>
              <Input data-testid="tariff-form-city" value={form.city}
                     onChange={(e) => upd("city", e.target.value)} placeholder="Es: Milano" /></div>
          )}
          {form.scope === "PROVINCE" && (
            <div><Label>Provincia (sigla)</Label>
              <Input data-testid="tariff-form-province" value={form.province}
                     onChange={(e) => upd("province", e.target.value.toUpperCase())}
                     placeholder="Es: MI" maxLength={4} /></div>
          )}
          {form.scope === "REGION" && (
            <div><Label>Regione</Label>
              <Input data-testid="tariff-form-region" value={form.region}
                     onChange={(e) => upd("region", e.target.value)} placeholder="Es: Lombardia" /></div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div><Label>Costo € / pallet</Label>
              <Input type="number" step="0.01" data-testid="tariff-form-cost"
                     value={form.cost_per_pallet} onChange={(e) => upd("cost_per_pallet", e.target.value)} /></div>
            <div><Label>Minimo trasporto (€)</Label>
              <Input type="number" step="0.01"
                     value={form.min_charge} onChange={(e) => upd("min_charge", e.target.value)}
                     placeholder="opzionale" /></div>
            <div><Label>Attiva</Label>
              <select value={form.is_active ? "1" : "0"}
                      onChange={(e) => upd("is_active", e.target.value === "1")}
                      style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}>
                <option value="1">Sì</option><option value="0">No</option>
              </select></div>
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Label>Scaglioni pallet (opzionali)</Label>
              <Button size="sm" onClick={addTier} data-testid="tariff-add-tier">+ Scaglione</Button>
            </div>
            {form.pallet_tiers.length === 0 ? (
              <p className="muted" style={{ margin: "4px 0", fontSize: ".8rem" }}>
                Nessuno scaglione — verrà applicato €{form.cost_per_pallet}/pallet fisso.
              </p>
            ) : form.pallet_tiers.map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginTop: 4, alignItems: "center" }}>
                <span style={{ fontSize: ".85rem", width: 60 }}>Fino a</span>
                <Input type="number" style={{ maxWidth: 100 }}
                       value={t.up_to ?? ""} onChange={(e) => updTier(i, "up_to", e.target.value)}
                       placeholder="∞" />
                <span style={{ fontSize: ".85rem" }}>pallet →</span>
                <Input type="number" step="0.01" style={{ maxWidth: 120 }}
                       value={t.cost_per_pallet} onChange={(e) => updTier(i, "cost_per_pallet", e.target.value)} />
                <span style={{ fontSize: ".85rem" }}>€/pallet</span>
                <Button size="sm" variant="danger" onClick={() => rmTier(i)}><X size={12} /></Button>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><Label>Giorni consegna MIN <span style={{ color: "#6b7280", fontSize: 11 }}>(transit time)</span></Label>
              <Input type="number" min={0} value={form.delivery_days_min}
                     onChange={(e) => upd("delivery_days_min", e.target.value)} /></div>
            <div><Label>Giorni consegna MAX <span style={{ color: "#6b7280", fontSize: 11 }}>(transit time)</span></Label>
              <Input type="number" min={0} value={form.delivery_days_max}
                     onChange={(e) => upd("delivery_days_max", e.target.value)} /></div>
          </div>
          <div>
            <Label>Giorni di ritiro/carico vettore</Label>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: -4, marginBottom: 6 }}>
              Giorni in cui il vettore ritira la merce dal nostro magazzino
            </div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {WEEKDAYS.map((d) => (
                <label key={d.v} style={{ padding: "4px 8px", border: "1px solid #cbd5e1",
                                          borderRadius: 4, cursor: "pointer",
                                          background: form.service_days.includes(d.v) ? "#dbeafe" : "#fff" }}>
                  <input type="checkbox" style={{ display: "none" }}
                         checked={form.service_days.includes(d.v)}
                         onChange={(e) => {
                           const s = new Set(form.service_days);
                           if (e.target.checked) s.add(d.v); else s.delete(d.v);
                           upd("service_days", [...s].sort());
                         }} />
                  {d.l}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><Label>Cut-off prenotazione ritiro (ore di anticipo)</Label>
              <Input type="number" min={0} placeholder="es. 24"
                     value={form.pickup_cutoff_hours ?? ""}
                     onChange={(e) => upd("pickup_cutoff_hours", e.target.value === "" ? null : Number(e.target.value))}
                     data-testid="pickup-cutoff-hours" />
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                Es. 24 = prenotazione entro le HH:MM del giorno precedente
              </div>
            </div>
            <div><Label>Ora cut-off (HH:MM)</Label>
              <Input placeholder="es. 15:00" value={form.pickup_cutoff_time ?? ""}
                     onChange={(e) => upd("pickup_cutoff_time", e.target.value || null)}
                     data-testid="pickup-cutoff-time" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div><Label>Valido dal</Label>
              <Input type="date" value={form.valid_from} onChange={(e) => upd("valid_from", e.target.value)} /></div>
            <div><Label>Valido al</Label>
              <Input type="date" value={form.valid_to} onChange={(e) => upd("valid_to", e.target.value)}
                     placeholder="opzionale" /></div>
          </div>
          <div>
            <Label>Note</Label>
            <Input value={form.notes} onChange={(e) => upd("notes", e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button variant="primary" onClick={save} loading={busy} disabled={busy}
                  data-testid="tariff-form-save">
            {busy ? "Salvataggio…" : "Salva"}
          </Button>
        </div>
      </div>
    </div>
  );
}
